import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, FileSpreadsheet, FileDown, LayoutList, Columns3, MessageSquarePlus, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, exportUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  STATI_COMMESSA, KANBAN_COLS, PRIORITA, PAGAMENTO, CATEGORIE_GARA, MACRO_AREE, STATI_ATTIVITA, TIPI_RILIEVO,
  statoCommessaColor, prioritaColor, pagamentoColor,
  deadlineStyle, deadlineLabel, giorniRimanenti, fmtData, fmtDataOra, fmtEuro,
} from "../lib/helpers";

const KANBAN_BG = {
  non_iniziata: "bg-slate-100",
  in_corso: "bg-blue-50",
  in_revisione: "bg-amber-50",
  consegnata: "bg-green-50",
};

const EMPTY_FORM = {
  denominazione: "", committente: "", macro_area: "privata", tipologia: "progettazione", priorita: "media",
  scadenza: "", operatore_id: "", responsabile_id: "", stato: "non_iniziata",
  importo: "", stato_pagamento: "da_fatturare", percentuale: 0, note: "", gara_id: "",
};

function CommessaForm({ open, onClose, onSaved, commessa, dipendenti, gareAggiudicate, isAdmin }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(commessa
        ? { ...EMPTY_FORM, ...commessa, importo: commessa.importo ?? "", operatore_id: commessa.operatore_id || "", responsabile_id: commessa.responsabile_id || "", gara_id: commessa.gara_id || "", scadenza: (commessa.scadenza || "").slice(0, 10) }
        : EMPTY_FORM);
    }
  }, [open, commessa]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      importo: form.importo === "" ? null : Number(form.importo),
      percentuale: Number(form.percentuale) || 0,
      operatore_id: form.operatore_id || null,
      responsabile_id: form.responsabile_id || null,
      gara_id: form.gara_id || null,
      scadenza: form.scadenza || null,
    };
    try {
      if (commessa) await api.put(`/commesse/${commessa.id}`, payload);
      else await api.post("/commesse", payload);
      toast.success(commessa ? "Commessa aggiornata" : "Commessa creata");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="commessa-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{commessa ? `Modifica commessa ${commessa.codice}` : "Nuova commessa"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>Denominazione / oggetto *</label>
            <input data-testid="commessa-form-denominazione" required value={form.denominazione} onChange={(e) => set("denominazione", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Area *</label>
            <Select value={form.macro_area} onValueChange={(v) => set("macro_area", v)}>
              <SelectTrigger data-testid="commessa-form-macro-area"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MACRO_AREE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.macro_area === "rilievi" && (
            <div>
              <label className={labelCls}>Tipo di rilievo</label>
              <Select value={form.tipo_rilievo} onValueChange={(v) => set("tipo_rilievo", v)}>
                <SelectTrigger data-testid="commessa-form-tipo-rilievo"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {TIPI_RILIEVO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className={labelCls}>Committente / cliente</label>
            <input data-testid="commessa-form-committente" value={form.committente} onChange={(e) => set("committente", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Tipologia incarico</label>
            <Select value={form.tipologia} onValueChange={(v) => set("tipologia", v)}>
              <SelectTrigger data-testid="commessa-form-tipologia"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIE_GARA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Priorità</label>
            <Select value={form.priorita} onValueChange={(v) => set("priorita", v)}>
              <SelectTrigger data-testid="commessa-form-priorita"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Scadenza consegna</label>
            <input data-testid="commessa-form-scadenza" type="date" value={form.scadenza} onChange={(e) => set("scadenza", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Operatore</label>
            <Select value={form.operatore_id} onValueChange={(v) => set("operatore_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="commessa-form-operatore"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Responsabile della pratica</label>
            <Select value={form.responsabile_id} onValueChange={(v) => set("responsabile_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="commessa-form-responsabile"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Stato avanzamento</label>
            <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
              <SelectTrigger data-testid="commessa-form-stato"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATI_COMMESSA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Avanzamento: {form.percentuale}%</label>
            <input data-testid="commessa-form-percentuale" type="range" min="0" max="100" step="5" value={form.percentuale} onChange={(e) => set("percentuale", e.target.value)} disabled={!!(commessa && (commessa.attivita || []).length)} className="w-full accent-blue-900 mt-2 disabled:opacity-40" />
            {commessa && (commessa.attivita || []).length > 0 && (
              <div className="text-[11px] text-slate-400 mt-1">Calcolato automaticamente dalle attività del registro</div>
            )}
          </div>
          {isAdmin && (
          <>
          <div>
            <label className={labelCls}>Importo incarico (€)</label>
            <input data-testid="commessa-form-importo" type="number" step="0.01" min="0" value={form.importo} onChange={(e) => set("importo", e.target.value)} className={`${inputCls} font-code`} />
          </div>
          <div>
            <label className={labelCls}>Stato pagamento</label>
            <Select value={form.stato_pagamento} onValueChange={(v) => set("stato_pagamento", v)}>
              <SelectTrigger data-testid="commessa-form-pagamento"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PAGAMENTO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          </>
          )}
          <div className="col-span-2">
            <label className={labelCls}>Collegamento a gara aggiudicata (tracciabilità)</label>
            <Select value={form.gara_id} onValueChange={(v) => set("gara_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="commessa-form-gara"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Nessun collegamento</SelectItem>
                {gareAggiudicate.map((g) => <SelectItem key={g.id} value={g.id}>{g.denominazione}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Note</label>
            <textarea data-testid="commessa-form-note" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} className={inputCls} />
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} data-testid="commessa-form-cancel" className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50">Annulla</button>
            <button type="submit" disabled={saving} data-testid="commessa-form-submit" className="px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
              {saving ? "Salvataggio…" : "Salva commessa"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CommessaDetail({ commessa, onClose, onSaved, dipendenti, isAdmin }) {
  const [c, setC] = useState(commessa);
  const [testo, setTesto] = useState("");
  const [saving, setSaving] = useState(false);
  const [nuovaAtt, setNuovaAtt] = useState({ titolo: "", assegnato_a: "", scadenza: "" });
  const [sendingMail, setSendingMail] = useState(false);

  useEffect(() => { setC(commessa); }, [commessa]);

  if (!c) return null;

  const nome = (id) => {
    const d = dipendenti.find((x) => x.id === id);
    return d ? `${d.nome} ${d.cognome}` : "—";
  };

  const applica = (updated) => { setC(updated); onSaved(); };

  const aggiungiNota = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/commesse/${c.id}/note`, { testo });
      toast.success("Nota aggiunta alla cronologia");
      setTesto("");
      applica(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const aggiungiAttivita = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/commesse/${c.id}/attivita`, {
        titolo: nuovaAtt.titolo,
        assegnato_a: nuovaAtt.assegnato_a || null,
        scadenza: nuovaAtt.scadenza || null,
      });
      toast.success("Attività aggiunta — email inviata all'assegnatario, se indicato");
      setNuovaAtt({ titolo: "", assegnato_a: "", scadenza: "" });
      applica(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleAttivita = async (a) => {
    try {
      const completata = a.stato !== "completata";
      const { data } = await api.patch(`/commesse/${c.id}/attivita/${a.id}`, completata ? { stato: "completata", percentuale: 100 } : { stato: "da_fare" });
      applica(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const salvaPercentuale = async (a, val) => {
    const perc = Math.min(100, Math.max(0, parseInt(val, 10) || 0));
    if (perc === (a.percentuale ?? 0)) return;
    try {
      const { data } = await api.patch(`/commesse/${c.id}/attivita/${a.id}`, { percentuale: perc });
      applica(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const eliminaAttivita = async (a) => {
    try {
      const { data } = await api.delete(`/commesse/${c.id}/attivita/${a.id}`);
      applica(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inviaEmail = async () => {
    setSendingMail(true);
    try {
      const { data } = await api.post(`/commesse/${c.id}/notifica`);
      if (data.sent) toast.success(`Email di assegnazione inviata a ${data.destinatario}`);
      else toast.error(data.motivo || "Invio fallito");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingMail(false);
    }
  };

  const eliminaCommessa = async () => {
    if (!window.confirm(`Eliminare definitivamente la commessa ${c.codice}?`)) return;
    try {
      await api.delete(`/commesse/${c.id}`);
      toast.success("Commessa eliminata");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls = "px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30";
  const gg = giorniRimanenti(c.scadenza);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="commessa-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
            <span className="font-code text-sm text-slate-500">{c.codice}</span>
            <span>{c.denominazione}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline" className={statoCommessaColor[c.stato]}>{STATI_COMMESSA[c.stato]}</Badge>
          <Badge variant="outline" className={prioritaColor[c.priorita]}>{PRIORITA[c.priorita]}</Badge>
          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">{MACRO_AREE[c.macro_area || "privata"]}</Badge>
          {gg !== null && <Badge variant="outline" className={`${deadlineStyle(gg).badge} font-code`}>{deadlineLabel(gg)} · {fmtData(c.scadenza)}</Badge>}
          {isAdmin && <Badge variant="outline" className={pagamentoColor[c.stato_pagamento]}>{PAGAMENTO[c.stato_pagamento]} · {fmtEuro(c.importo)}</Badge>}
        </div>
        {(c.attivita || []).length > 0 && (
          <div className="flex items-center gap-2" data-testid="detail-avanzamento-auto">
            <Progress value={c.percentuale} className="h-2 flex-1 [&>div]:bg-blue-900" />
            <span className="text-xs font-code text-slate-600 shrink-0">{c.percentuale}% · calcolato dalle attività</span>
          </div>
        )}
        <div className="text-xs text-slate-500">
          Operatore: <span className="text-slate-700 font-medium">{nome(c.operatore_id)}</span>
          <span className="mx-2">·</span>
          Responsabile: <span className="text-slate-700 font-medium">{nome(c.responsabile_id)}</span>
          <span className="mx-2">·</span>
          Committente: <span className="text-slate-700 font-medium">{c.committente || "—"}</span>
        </div>

        <section className="border border-slate-200 rounded-md" data-testid="attivita-section">
          <header className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Registro attività — ordine cronologico</h3>
            <span className="text-xs font-code text-slate-500">{(c.attivita || []).filter((a) => a.stato === "completata").length}/{(c.attivita || []).length} completate</span>
          </header>
          <div className="divide-y divide-slate-100">
            {(c.attivita || []).length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-500">Nessuna attività inserita (es. redazione APE, accatastamento, computo metrico…).</div>
            )}
            {(c.attivita || []).slice().sort((x, y) => (x.assegnato_il || x.created_at || "").localeCompare(y.assegnato_il || y.created_at || "")).map((a) => (
              <div key={a.id} data-testid={`attivita-row-${a.id}`} className="flex items-center gap-3 px-3 py-2">
                <input type="checkbox" data-testid={`attivita-toggle-${a.id}`} checked={a.stato === "completata"} onChange={() => toggleAttivita(a)} className="h-4 w-4 accent-blue-900 cursor-pointer" />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${a.stato === "completata" ? "line-through text-slate-400" : "text-slate-800 font-medium"}`}>{a.titolo}</div>
                  <div className="text-xs text-slate-500">
                    Assegnata a <span className="font-medium text-slate-600">{a.assegnato_a ? nome(a.assegnato_a) : "—"}</span> il {fmtDataOra(a.assegnato_il || a.created_at)}
                    {a.scadenza ? ` · scadenza ${fmtData(a.scadenza)}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <input
                    type="number" min="0" max="100" step="5"
                    key={`${a.id}-${a.percentuale ?? 0}`}
                    defaultValue={a.percentuale ?? 0}
                    data-testid={`attivita-perc-${a.id}`}
                    onBlur={(e) => salvaPercentuale(a, e.target.value)}
                    className="w-16 px-1.5 py-1 text-xs font-code text-center bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30"
                  />
                  <span className="text-xs text-slate-400 font-code">%</span>
                </div>
                <Badge variant="outline" className={a.stato === "completata" ? "bg-green-100 text-green-800 border-green-200" : a.stato === "in_corso" ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                  {STATI_ATTIVITA[a.stato] || a.stato}
                </Badge>
                {isAdmin && (
                  <button data-testid={`attivita-delete-${a.id}`} onClick={() => eliminaAttivita(a)} className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-700">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={aggiungiAttivita} className="flex items-center gap-2 px-3 py-2.5 border-t border-slate-200 bg-slate-50/50 flex-wrap">
            <input data-testid="attivita-titolo-input" required value={nuovaAtt.titolo} onChange={(e) => setNuovaAtt((n) => ({ ...n, titolo: e.target.value }))}
              placeholder="Nuova attività (es. redazione APE, accatastamento…)" className={`flex-1 min-w-48 ${inputCls}`} />
            <Select value={nuovaAtt.assegnato_a} onValueChange={(v) => setNuovaAtt((n) => ({ ...n, assegnato_a: v === "__none" ? "" : v }))}>
              <SelectTrigger data-testid="attivita-assegnato-select" className="w-44 h-9 text-sm bg-white"><SelectValue placeholder="Assegnata a…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Non assegnata</SelectItem>
                {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="date" data-testid="attivita-scadenza-input" value={nuovaAtt.scadenza} onChange={(e) => setNuovaAtt((n) => ({ ...n, scadenza: e.target.value }))} className={`w-36 ${inputCls}`} />
            <button type="submit" disabled={saving} data-testid="attivita-add-submit" className="px-3 py-2 text-xs font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
              Aggiungi
            </button>
          </form>
        </section>

        <section data-testid="cronologia-section">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Cronologia attività e comunicazioni</h3>
          <div className="max-h-48 overflow-y-auto space-y-2 mb-3">
            {(c.cronologia || []).length === 0 && <div className="text-sm text-slate-500">Nessuna attività registrata.</div>}
            {(c.cronologia || []).slice().reverse().map((n, i) => (
              <div key={i} className="text-sm border-l-2 border-blue-900/30 pl-3 py-1">
                <div className="text-xs text-slate-400 font-code">{fmtDataOra(n.data)} · {n.autore}</div>
                <div className="text-slate-700">{n.testo}</div>
              </div>
            ))}
          </div>
          <form onSubmit={aggiungiNota} className="flex gap-2">
            <input data-testid="note-input" required value={testo} onChange={(e) => setTesto(e.target.value)}
              placeholder="Nuova nota / comunicazione…"
              className="flex-1 px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30" />
            <button type="submit" disabled={saving} data-testid="note-submit" className="px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
              Aggiungi
            </button>
          </form>
        </section>

        {isAdmin && (
          <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-200">
            <button data-testid="commessa-detail-delete" onClick={eliminaCommessa}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4" /> Elimina commessa
            </button>
            <button data-testid="commessa-send-email-button" onClick={inviaEmail} disabled={sendingMail}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950 disabled:opacity-60">
              <MailCheck className="h-4 w-4" /> {sendingMail ? "Invio in corso…" : "Invia email all'operatore"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Commesse() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [commesse, setCommesse] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [gare, setGare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState("tabella");
  const [filtroStato, setFiltroStato] = useState("tutte");
  const [filtroPriorita, setFiltroPriorita] = useState("tutte");
  const [filtroOperatore, setFiltroOperatore] = useState("tutti");
  const [macroTab, setMacroTab] = useState("tutte");
  const [soloMie, setSoloMie] = useState(false);
  const [mostraArchivio, setMostraArchivio] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [noteCommessa, setNoteCommessa] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d, g] = await Promise.all([api.get("/commesse"), api.get("/dipendenti/lookup"), api.get("/gare")]);
      setCommesse(c.data);
      setDipendenti(d.data);
      setGare(g.data.filter((x) => x.stato === "aggiudicata"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get("nuova") === "1") {
      setEditing(null);
      setDialogOpen(true);
      searchParams.delete("nuova");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, isAdmin, setSearchParams]);

  const filtrate = useMemo(() => {
    return commesse.filter((c) => {
      if (!mostraArchivio && c.stato === "chiusa") return false;
      if (filtroStato !== "tutte" && c.stato !== filtroStato) return false;
      if (filtroPriorita !== "tutte" && c.priorita !== filtroPriorita) return false;
      if (soloMie && user?.dipendente_id && c.operatore_id !== user.dipendente_id && c.responsabile_id !== user.dipendente_id) return false;
      if (macroTab !== "tutte" && (c.macro_area || "privata") !== macroTab) return false;
      if (filtroOperatore !== "tutti" && c.operatore_id !== filtroOperatore && c.responsabile_id !== filtroOperatore) return false;
      if (q && !`${c.codice} ${c.denominazione} ${c.committente}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [commesse, mostraArchivio, filtroStato, filtroPriorita, filtroOperatore, macroTab, soloMie, q]);

  const conteggiAree = useMemo(() => {
    const base = commesse.filter((c) => mostraArchivio || c.stato !== "chiusa");
    const o = { tutte: base.length };
    Object.keys(MACRO_AREE).forEach((k) => { o[k] = base.filter((c) => (c.macro_area || "privata") === k).length; });
    return o;
  }, [commesse, mostraArchivio]);

  const nome = (id) => {
    const d = dipendenti.find((x) => x.id === id);
    return d ? `${d.nome} ${d.cognome}` : "—";
  };

  const elimina = async (c) => {
    if (!window.confirm(`Eliminare la commessa ${c.codice}?`)) return;
    try {
      await api.delete(`/commesse/${c.id}`);
      toast.success("Commessa eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDrop = async (e, nuovoStato) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const c = commesse.find((x) => x.id === id);
    if (!c || c.stato === nuovoStato) return;
    try {
      await api.patch(`/commesse/${id}`, { stato: nuovoStato });
      setCommesse((prev) => prev.map((x) => (x.id === id ? { ...x, stato: nuovoStato } : x)));
      toast.success(`${c.codice} → ${STATI_COMMESSA[nuovoStato]}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const CardCommessa = ({ c }) => {
    const gg = giorniRimanenti(c.scadenza);
    const st = deadlineStyle(gg);
    return (
      <div
        draggable={isAdmin || user?.dipendente_id === c.operatore_id || user?.dipendente_id === c.responsabile_id}
        onDragStart={(e) => e.dataTransfer.setData("text/plain", c.id)}
        data-testid={`kanban-card-${c.id}`}
        className="bg-white border border-slate-200 rounded-md p-3 shadow-sm cursor-grab active:cursor-grabbing hover:border-blue-900/40 transition-colors"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-code text-[11px] text-slate-500">{c.codice}</span>
          <Badge variant="outline" className={`${prioritaColor[c.priorita]} text-[10px]`}>{PRIORITA[c.priorita]}</Badge>
        </div>
        <div className="text-sm font-medium text-slate-800 mt-1 leading-snug">{c.denominazione}</div>
        <div className="text-xs text-slate-500 mt-0.5">{c.committente}</div>
        <Progress value={c.percentuale} className="h-1.5 mt-2 [&>div]:bg-blue-900" />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-slate-500">{nome(c.operatore_id)}</span>
          {gg !== null && <Badge variant="outline" className={`${st.badge} font-code text-[10px]`}>{deadlineLabel(gg)}</Badge>}
        </div>
        {isAdmin && (
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
            <Badge variant="outline" className={`${pagamentoColor[c.stato_pagamento]} text-[10px]`}>{PAGAMENTO[c.stato_pagamento]}</Badge>
            <span className="font-code text-[11px] text-slate-600">{fmtEuro(c.importo)}</span>
          </div>
        )}
        <div className="flex justify-end gap-1 mt-2">
          <button data-testid={`kanban-note-${c.id}`} onClick={() => setNoteCommessa(c)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><MessageSquarePlus className="h-3.5 w-3.5" /></button>
          <button data-testid={`kanban-edit-${c.id}`} onClick={() => (isAdmin || user?.dipendente_id === c.operatore_id || user?.dipendente_id === c.responsabile_id) ? (setEditing(c), setDialogOpen(true)) : setNoteCommessa(c)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><Pencil className="h-3.5 w-3.5" /></button>
          {isAdmin && (
            <button data-testid={`kanban-delete-${c.id}`} onClick={() => elimina(c)} className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="commesse-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">Commesse</h1>
          <p className="text-sm text-slate-500 mt-1">{commesse.filter((c) => c.stato !== "chiusa").length} attive · {commesse.filter((c) => c.stato === "chiusa").length} in archivio</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={exportUrl("commesse", "excel")} data-testid="export-commesse-excel-button"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </a>
          <a href={exportUrl("commesse", "pdf")} data-testid="export-commesse-pdf-button"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
            <FileDown className="h-4 w-4" /> PDF
          </a>
          <div className="flex border border-slate-300 rounded-md overflow-hidden">
            <button data-testid="commesse-view-toggle-table" onClick={() => setVista("tabella")}
              className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${vista === "tabella" ? "bg-blue-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <LayoutList className="h-4 w-4" /> Tabella
            </button>
            <button data-testid="commesse-view-toggle-kanban" onClick={() => setVista("kanban")}
              className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${vista === "kanban" ? "bg-blue-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <Columns3 className="h-4 w-4" /> Kanban
            </button>
          </div>
          <button data-testid="commesse-add-modal-trigger" onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950">
            <Plus className="h-4 w-4" /> Nuova commessa
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-md px-4 py-3">
        <Select value={filtroStato} onValueChange={setFiltroStato}>
          <SelectTrigger data-testid="commesse-filter-stato" className="w-44 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutti gli stati</SelectItem>
            {Object.entries(STATI_COMMESSA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroPriorita} onValueChange={setFiltroPriorita}>
          <SelectTrigger data-testid="commesse-filter-priorita" className="w-40 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le priorità</SelectItem>
            {Object.entries(PRIORITA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroOperatore} onValueChange={setFiltroOperatore}>
          <SelectTrigger data-testid="commesse-filter-operatore" className="w-48 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli operatori</SelectItem>
            {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="commesse-filter-mie" checked={soloMie} onChange={(e) => setSoloMie(e.target.checked)} className="h-3.5 w-3.5 accent-blue-900" />
          Solo le mie pratiche
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer ml-auto">
          <input type="checkbox" data-testid="commesse-show-archive" checked={mostraArchivio} onChange={(e) => setMostraArchivio(e.target.checked)} className="h-3.5 w-3.5 accent-blue-900" />
          Mostra archivio (chiuse)
        </label>
      </div>

      <div className="flex gap-1 border-b border-slate-200" data-testid="commesse-macro-tabs">
        {Object.entries({ tutte: "Tutte le aree", ...MACRO_AREE }).map(([k, v]) => (
          <button key={k} data-testid={`commesse-tab-${k}`} onClick={() => setMacroTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              macroTab === k ? "border-blue-900 text-blue-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {v} <span className="font-code text-xs text-slate-400">({conteggiAree[k]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500 text-sm" data-testid="commesse-loading">Caricamento commesse…</div>
      ) : vista === "kanban" ? (
        <div className="grid grid-cols-4 gap-4 items-start" data-testid="commesse-kanban">
          {KANBAN_COLS.map((col) => {
            const items = filtrate.filter((c) => c.stato === col);
            return (
              <div
                key={col}
                data-testid={`kanban-col-${col}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, col)}
                className={`${KANBAN_BG[col]} rounded-md p-3 min-h-64 border border-slate-200`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">{STATI_COMMESSA[col]}</h3>
                  <span className="text-xs font-code text-slate-500">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((c) => <CardCommessa key={c.id} c={c} />)}
                  {items.length === 0 && <div className="text-xs text-slate-400 text-center py-6">Trascina qui una commessa</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="commesse-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Codice", "Commessa", "Priorità", "Scadenza", "Operatore", "Responsabile", "Stato", "Avanz.", ...(isAdmin ? ["Importo", "Pagamento"] : []), ""].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrate.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-500">Nessuna commessa trovata.</td></tr>
              )}
              {filtrate.map((c) => {
                const gg = giorniRimanenti(c.scadenza);
                const inRitardo = gg !== null && gg < 0 && !["consegnata", "chiusa"].includes(c.stato);
                const st = deadlineStyle(inRitardo ? gg : (["consegnata", "chiusa"].includes(c.stato) ? null : gg));
                return (
                  <tr key={c.id} data-testid={`commessa-row-${c.id}`} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/70 ${st.row}`}>
                    <td className="px-3 py-2.5 font-code text-xs text-slate-600 whitespace-nowrap">{c.codice}</td>
                    <td className="px-3 py-2.5 max-w-64">
                      <div className="font-medium text-slate-800 truncate">{c.denominazione}</div>
                      <div className="text-xs text-slate-500">{c.committente} · {MACRO_AREE[c.macro_area || "privata"]}{c.gara_id ? " · da gara" : ""}</div>
                    </td>
                    <td className="px-3 py-2.5"><Badge variant="outline" className={prioritaColor[c.priorita]}>{PRIORITA[c.priorita]}</Badge></td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="text-xs text-slate-700">{fmtData(c.scadenza)}</div>
                      {gg !== null && !["consegnata", "chiusa"].includes(c.stato) && (
                        <Badge variant="outline" className={`${st.badge} font-code mt-0.5`}>{deadlineLabel(gg)}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{nome(c.operatore_id)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{nome(c.responsabile_id)}</td>
                    <td className="px-3 py-2.5"><Badge variant="outline" className={statoCommessaColor[c.stato]}>{STATI_COMMESSA[c.stato]}</Badge></td>
                    <td className="px-3 py-2.5 w-28">
                      <div className="flex items-center gap-1.5">
                        <Progress value={c.percentuale} className="h-1.5 flex-1 [&>div]:bg-blue-900" />
                        <span className="text-[11px] font-code text-slate-500">{c.percentuale}%</span>
                      </div>
                    </td>
                    {isAdmin && <td className="px-3 py-2.5 font-code text-xs text-slate-700 whitespace-nowrap">{fmtEuro(c.importo)}</td>}
                    {isAdmin && <td className="px-3 py-2.5"><Badge variant="outline" className={pagamentoColor[c.stato_pagamento]}>{PAGAMENTO[c.stato_pagamento]}</Badge></td>}
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <button data-testid={`commessa-note-${c.id}`} onClick={() => setNoteCommessa(c)} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><MessageSquarePlus className="h-3.5 w-3.5" /></button>
                      {(isAdmin || user?.dipendente_id === c.operatore_id || user?.dipendente_id === c.responsabile_id) && (
                        <button data-testid={`commessa-edit-${c.id}`} onClick={() => { setEditing(c); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><Pencil className="h-3.5 w-3.5" /></button>
                      )}
                      {isAdmin && (
                        <button data-testid={`commessa-delete-${c.id}`} onClick={() => elimina(c)} className="p-1.5 rounded hover:bg-red-100 text-slate-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CommessaForm open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} commessa={editing} dipendenti={dipendenti} gareAggiudicate={gare} isAdmin={isAdmin} />
      <CommessaDetail commessa={noteCommessa} onClose={() => setNoteCommessa(null)} onSaved={load} dipendenti={dipendenti} isAdmin={isAdmin} />
    </div>
  );
}
